import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Layout from "./components/Layout";
import SEOHead from "./components/SEOHead";
import ScrollToTop from "./components/ScrollToTop";
import HomePage from "./pages/HomePage";
import LocationPage from "./pages/LocationPage";
import MenuPage from "./pages/MenuPage";
import OrderPage from "./pages/OrderPage";
import ReservationsPage from "./pages/ReservationsPage";
import NotFoundPage from "./pages/NotFoundPage";
import AdminPage from "./pages/AdminPage";
import { menuConfigs, getNavSections, Location } from "./data/menuConfig";
import { initGA, trackPageView } from "./utils/analytics";

// Helper to get all locations
const locations: Location[] = Object.keys(menuConfigs) as Location[];

// Helper to get default section for a location (first in nav order)
const getDefaultSection = (location: Location): string => {
  const sections = getNavSections(location);
  return sections[0] as string;
};

// Analytics tracker component
const AnalyticsTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Initialize GA on mount
    initGA();
  }, []);

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname);
  }, [location]);

  return null;
};

const App: React.FC = () => {
  return (
    <Router>
      <AnalyticsTracker />
      <SEOHead />
      <ScrollToTop />
      <Routes>
        {/* Admin Route — full-bleed, no public layout chrome */}
        <Route path="/admin" element={<AdminPage />} />

        {/* All other routes wrapped in the public site layout */}
        <Route
          path="*"
          element={
            <Layout>
              <Routes>
                <Route path="/" element={<HomePage />} />

                {locations.map((location) => {
                  const sections = getNavSections(location);
                  const defaultSection = getDefaultSection(location);

                  return (
                    <React.Fragment key={location}>
                      <Route
                        path={`/${location}`}
                        element={<LocationPage location={location} />}
                      />
                      <Route
                        path={`/${location}/menu`}
                        element={
                          <MenuPage location={location} section={defaultSection} />
                        }
                      />
                      {sections.map((section) => (
                        <Route
                          key={`${location}-${section}`}
                          path={`/${location}/menu/${section}`}
                          element={
                            <MenuPage
                              location={location}
                              section={section as string}
                            />
                          }
                        />
                      ))}
                    </React.Fragment>
                  );
                })}

                <Route path="/order" element={<OrderPage />} />
                <Route path="/reservations" element={<ReservationsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Layout>
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
